// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: small shared pieces of team threads: level names and icons, faces, and the errors sharing
// refuses with.

import styles from '@app/features/konvo_agents/components/AgentShare.module.css';
import {agentErrorMessage} from '@app/features/konvo_agents/components/AgentsCopy';
import {StatusAwareAvatar} from '@app/features/ui/components/StatusAwareAvatar';
import Users from '@app/features/user/state/Users';
import type {AgentShareLevel} from '@fluxer/schema/src/domains/konvo/AgentShares';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {IconEye, IconMessage, IconSend} from '@tabler/icons-react';
import {observer} from 'mobx-react-lite';

const LEVEL_DESCRIPTORS = {
	watch: msg({message: 'Watch', comment: 'Agents team sharing: the level that reads a shared thread live.'}),
	comment: msg({message: 'Comment', comment: 'Agents team sharing: the level that can pin comments to turns.'}),
	prompt: msg({message: 'Prompt', comment: 'Agents team sharing: the level that can prompt the agent.'}),
} as const;

const CAN_DESCRIPTORS = {
	watch: msg({message: 'can watch', comment: 'Agents team sharing: what a person on a shared thread may do.'}),
	comment: msg({message: 'can comment', comment: 'Agents team sharing: what a person on a shared thread may do.'}),
	prompt: msg({message: 'can prompt', comment: 'Agents team sharing: what a person on a shared thread may do.'}),
} as const;

const CAN_TITLE_DESCRIPTORS = {
	watch: msg({message: 'Can watch', comment: 'Agents team sharing: a person’s level on the level button.'}),
	comment: msg({message: 'Can comment', comment: 'Agents team sharing: a person’s level on the level button.'}),
	prompt: msg({message: 'Can prompt', comment: 'Agents team sharing: a person’s level on the level button.'}),
} as const;

export const levelName = (i18n: I18n, level: AgentShareLevel) => i18n._(LEVEL_DESCRIPTORS[level]);
export const canTitle = (i18n: I18n, level: AgentShareLevel) => i18n._(CAN_TITLE_DESCRIPTORS[level]);
export const canName = (i18n: I18n, level: AgentShareLevel) => i18n._(CAN_DESCRIPTORS[level]);

export function LevelIcon({level, size = 14}: {level: AgentShareLevel; size?: number}) {
	if (level === 'watch') return <IconEye size={size} stroke={1.7} />;
	if (level === 'comment') return <IconMessage size={size} stroke={1.7} />;
	return <IconSend size={size} stroke={1.7} />;
}

/** Team sharing's own refusals, then the usual Agents ones. */
export function shareErrorMessage(i18n: I18n, error: unknown): string {
	const body = error && typeof error === 'object' ? (error as {body?: unknown}).body : null;
	const code = body && typeof body === 'object' ? (body as {code?: unknown}).code : null;
	const message = body && typeof body === 'object' ? (body as {message?: unknown}).message : null;
	if (
		typeof message === 'string' &&
		(code === 'SHARE_NOT_ALLOWED' ||
			code === 'SHARE_APPROVAL_MODE' ||
			code === 'SHARING_OFF' ||
			code === 'SHARE_BAD_PERSON')
	)
		return message;
	return agentErrorMessage(i18n, error);
}

/** A face by user id: their avatar when Konvo knows them, else initials. */
export const Face = observer(
	({userId, name, size = 26, live}: {userId: string; name: string; size?: number; live?: boolean}) => {
		const user = Users.getUser(userId);
		const initials = name
			.split(/\s+/)
			.map((part) => part[0])
			.filter(Boolean)
			.slice(0, 2)
			.join('')
			.toUpperCase();
		return (
			<span className={styles.face} style={{width: size, height: size}} title={name}>
				{user ? <StatusAwareAvatar user={user} size={size} disablePresence /> : initials}
				{live && <span className={styles.faceLive} />}
			</span>
		);
	},
);
